// ClientContactActions — reusable Call · Audio · WhatsApp buttons per client/customer row.
// Audio flow: pick file OR record voice-note → downloads to seller's device → opens wa.me/PHONE (seller manually attaches).
import React, { useRef, useState } from 'react';
import { Phone, Mic, MessageCircle, Upload, Square } from 'lucide-react';
import { toast } from 'sonner';

const digits = (p) => String(p || '').replace(/[^0-9]/g, '');

export default function ClientContactActions({ phone, name = '', message = '', size = 'sm', testId = 'client-actions' }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const p = digits(phone);
  const disabled = !p;
  const btnCls = size === 'xs'
    ? 'w-7 h-7 rounded-lg flex items-center justify-center'
    : 'w-9 h-9 rounded-lg flex items-center justify-center';
  const iconSize = size === 'xs' ? 12 : 14;

  const openWA = (autoText) => {
    const txt = encodeURIComponent(autoText || message || `Namaste ${name || ''}`.trim());
    window.open(`https://wa.me/${p}?text=${txt}`, '_blank');
  };

  const downloadAndOpenWA = (blob, filename) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    openWA(`🎙 Voice-note aa raha hai (attached) — ${name || ''}`.trim());
    toast.success('🎙 Audio download ho gaya · WhatsApp me 📎 se attach karo');
  };

  const pickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      // Try native share with the specific file first
      const file = new File([f], f.name, { type: f.type || 'audio/mpeg' });
      const payload = { title: 'Voice message', text: message || `For ${name || 'customer'}`, files: [file] };
      if (navigator.canShare && navigator.canShare(payload)) {
        try { await navigator.share(payload); toast.success('✅ Shared'); return; }
        catch (err) { if (err && err.name === 'AbortError') return; }
      }
      downloadAndOpenWA(f, f.name);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mime || 'audio/webm';
        const ext = type.includes('ogg') || type.includes('opus') ? 'opus' : (type.includes('mp4') ? 'm4a' : 'webm');
        const blob = new Blob(chunksRef.current, { type });
        const filename = `voice-${(name || 'client').replace(/\W+/g, '_')}-${Date.now()}.${ext}`;
        // Try native share first
        try {
          const file = new File([blob], filename, { type });
          const payload = { title: 'Voice note', text: message || `For ${name || 'customer'}`, files: [file] };
          if (navigator.canShare && navigator.canShare(payload)) {
            await navigator.share(payload);
            toast.success('✅ Voice shared');
            return;
          }
        } catch (err) { if (err && err.name === 'AbortError') return; }
        downloadAndOpenWA(blob, filename);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      toast('🔴 Recording… tap Stop when done', { icon: '🎙' });
    } catch { toast.error('Mic access denied'); }
  };
  const stopRecord = () => {
    try { recorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  if (disabled) return null;
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <a href={`tel:+${p}`} title={`Call ${name || phone}`}
         data-testid={`${testId}-call`}
         className={`${btnCls} bg-sky-600 hover:bg-sky-700 text-white`}>
        <Phone size={iconSize} />
      </a>
      {recording ? (
        <button onClick={stopRecord} title="Stop recording"
                data-testid={`${testId}-stop`}
                className={`${btnCls} bg-red-600 animate-pulse text-white`}>
          <Square size={iconSize} />
        </button>
      ) : (
        <button onClick={startRecord} title="Record voice-note for this client"
                data-testid={`${testId}-record`}
                disabled={busy}
                className={`${btnCls} bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50`}>
          <Mic size={iconSize} />
        </button>
      )}
      <button onClick={() => fileRef.current?.click()} title="Send existing audio file"
              data-testid={`${testId}-audio-file`}
              disabled={busy}
              className={`${btnCls} bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:opacity-50`}>
        <Upload size={iconSize} />
      </button>
      <input ref={fileRef} type="file" accept="audio/*,.mp3,.ogg,.opus,.wav,.m4a,.webm" onChange={pickFile} className="hidden" />
      <a href={`https://wa.me/${p}${message ? `?text=${encodeURIComponent(message)}` : ''}`} target="_blank" rel="noreferrer"
         title={`WhatsApp ${name || phone}`}
         data-testid={`${testId}-wa`}
         className={`${btnCls} bg-green-600 hover:bg-green-700 text-white`}>
        <MessageCircle size={iconSize} />
      </a>
    </div>
  );
}
